import Link from "next/link";
import Image from "next/image";
import { Container } from "../primitives/Container";
import { isExternal } from "../primitives/Button";
import type { BrandLike, FooterGroup, NavChild } from "./types";

function FLink({ link }: { link: NavChild }) {
  const cls = "text-sm text-white/70 transition-colors hover:text-signal";
  return isExternal(link.href)
    ? <a href={link.href} className={cls} {...(link.href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})}>{link.label}</a>
    : <Link href={link.href} className={cls}>{link.label}</Link>;
}

export function SiteFooter({ brand, footer, legal }: { brand: BrandLike; footer: FooterGroup[]; legal: NavChild[] }) {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-ink">
      <Container>
        <div className="grid gap-12 py-16 md:grid-cols-12 md:py-20">
          <div className="md:col-span-5">
            <Link href="/" aria-label={`${brand.name} — home`} className="inline-block font-display text-xl font-semibold tracking-tight text-paper-raised">
              {brand.slug === "pscx" ? <Image src={brand.logo.src} alt={brand.logo.alt} width={brand.logo.width} height={brand.logo.height} className="h-16 w-auto" /> : brand.name}
            </Link>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-white/70">{brand.footer.blurb}</p>
            {brand.footer.loop.length ? (
              <p className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 font-display text-sm font-semibold text-signal" aria-label={brand.footer.loop.join(", then ")}>
                {brand.footer.loop.map((step, i) => (
                  <span key={step} className="inline-flex items-center gap-2">
                    {step}{i < brand.footer.loop.length - 1 ? <span aria-hidden="true" className="text-signal/70">→</span> : null}
                  </span>
                ))}
              </p>
            ) : null}
          </div>
          {footer.map((group) => (
            <nav key={group.heading} aria-label={group.heading} className={footer.length > 1 ? "md:col-span-3" : "md:col-span-7"}>
              <h2 className="mb-4 font-display text-sm font-semibold uppercase tracking-[0.16em] text-paper-raised">{group.heading}</h2>
              <ul className={`space-y-3 ${footer.length === 1 && group.links.length > 5 ? "sm:columns-2" : ""}`}>
                {group.links.map((l) => <li key={l.href}><FLink link={l} /></li>)}
              </ul>
            </nav>
          ))}
        </div>
        <div className="flex flex-col gap-4 border-t border-white/10 py-7 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-white/55">© {year} {brand.name}. All rights reserved.</p>
          <ul className="flex flex-wrap items-center gap-x-6 gap-y-2">
            {legal.map((l) => <li key={l.href}><FLink link={l} /></li>)}
            {Object.entries(brand.social).map(([k, href]) => <li key={k}>{k.toLowerCase() === "linkedin" ? (
              <a href={href} aria-label={`${brand.name} on LinkedIn (opens in a new tab)`} target="_blank" rel="noopener noreferrer" className="inline-flex size-11 items-center justify-center text-white/70 transition-opacity hover:opacity-70 focus-visible:rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-signal">
                <svg aria-hidden="true" viewBox="0 0 24 24" className="size-5" fill="currentColor"><path d="M20.45 2H3.55C2.69 2 2 2.68 2 3.52v16.96C2 21.32 2.69 22 3.55 22h16.9c.86 0 1.55-.68 1.55-1.52V3.52C22 2.68 21.31 2 20.45 2ZM7.93 18.75H4.98V9.2h2.95v9.55ZM6.45 7.89a1.71 1.71 0 1 1 0-3.42 1.71 1.71 0 0 1 0 3.42ZM19 18.75h-2.95v-4.64c0-1.11-.02-2.53-1.54-2.53-1.54 0-1.78 1.2-1.78 2.45v4.72H9.78V9.2h2.83v1.3h.04c.39-.74 1.36-1.52 2.8-1.52 3 0 3.55 1.97 3.55 4.53v5.24Z" /></svg>
              </a>
            ) : <FLink link={{ label: k[0]!.toUpperCase() + k.slice(1), href }} />}</li>)}
          </ul>
        </div>
      </Container>
    </footer>
  );
}
